/**
 * Qué del borrador del asistente de Reactivación puede quedar en el navegador una vez analizada la campaña.
 *
 * El borrador vive en localStorage para que un F5 no borre el avance. Antes de analizar guarda los dos CSV
 * crudos (nombres, mails, celulares de los clientes de la marca) y lo que devolvió `parse`, que trae 3 filas
 * de muestra por archivo y un valor de ejemplo por columna. Después de analizar ya no se borraba nada de eso:
 * si nadie volvía a abrir la página, quedaba ahí indefinidamente, en la compu del equipo de Deenex.
 *
 * Analizada la campaña, los datos viven en el backend: el borrador conserva sólo lo que hace falta para
 * retomar (el mapeo confirmado, los encabezados y los totales), sin ninguna fila de clientes. Los archivos de
 * ejemplo son la excepción: vienen con la app (no son datos de nadie) y con ellos se sabe que es el ejemplo.
 */

/** Las dos claves del borrador llevan la marca después de este prefijo (`CampanaNuevaRealPage`). */
export const PREFIJO_BORRADOR = "deenex-activa-borrador:";
export const PREFIJO_BORRADOR_ARCHIVOS = "deenex-activa-borrador-files:";

type Sugerencia = { ejemplo?: unknown; [k: string]: unknown };
type ArchivoParse = { preview?: unknown; sugerencia?: Sugerencia[]; [k: string]: unknown };

/** `parseData` sin las filas de muestra ni el valor de ejemplo de cada columna. */
export function parseDataSinMuestras<T>(pd: T): T {
  if (!pd || typeof pd !== "object") return pd;
  const out: Record<string, unknown> = { ...(pd as Record<string, unknown>) };
  for (const arch of ["pedidos", "clientes"]) {
    const a = out[arch] as ArchivoParse | undefined;
    if (!a || typeof a !== "object") continue;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { preview, ...resto } = a;
    out[arch] = {
      ...resto,
      ...(Array.isArray(a.sugerencia) ? { sugerencia: a.sugerencia.map((s) => ({ ...s, ejemplo: "" })) } : {}),
    };
  }
  return out as T;
}

/** Si los CSV crudos pueden seguir guardados en el navegador: sólo antes de analizar, o si son los de ejemplo. */
export function puedeGuardarArchivos(o: { analizada: boolean; esEjemplo: boolean }): boolean {
  return !o.analizada || o.esEjemplo;
}

/**
 * Al cambiar de persona en el navegador (entrar, salir o que venza la sesión: `olvidarSesionActiva`) se
 * borra lo que tiene datos de clientes, de todas las marcas: los CSV crudos y el borrador de antes de
 * analizar, que guarda las filas de muestra de `parse`. Antes, cerrar sesión no los tocaba y quien usaba la
 * compu después los tenía en el navegador. El borrador de una campaña ya analizada se queda, sin muestras:
 * no tiene datos de nadie y quien vuelve retoma donde estaba (si hace falta procesar de nuevo, el asistente
 * pide volver a subir los archivos).
 */
export function olvidarDatosDeClientes(storage: Storage): void {
  const claves: string[] = [];
  for (let i = 0; i < storage.length; i++) {
    const k = storage.key(i);
    if (k) claves.push(k);
  }
  for (const k of claves) {
    if (k.startsWith(PREFIJO_BORRADOR_ARCHIVOS)) { storage.removeItem(k); continue; }
    if (!k.startsWith(PREFIJO_BORRADOR)) continue;
    try {
      const b = JSON.parse(storage.getItem(k) || "null");
      if (!b?.campaign) { storage.removeItem(k); continue; }
      storage.setItem(k, JSON.stringify({ ...b, parseData: parseDataSinMuestras(b.parseData) }));
    } catch { storage.removeItem(k); }
  }
}
